import type {
  ObjectiveConstraint,
  ObjectivePlanBlock,
  ObjectivePlanLineupGroup,
} from "@repo/shared/api";
import type { TranslateFn } from "@repo/shared/i18n";

const TECHNIQUES = [
  "header",
  "finesse",
  "low-driven",
  "power",
  "chip",
  "volley",
  "free-kick",
  "penalty",
  "outside-the-box",
  "cross",
  "through-ball",
  "backheel",
  "other",
] as const;

const playerLabel = (constraints: ObjectiveConstraint[]) =>
  constraints.map((c) => c.label).join(" ");

const PLAIN = ["goals", "assists", "goalsOrAssists"] as const;

/**
 * One thing to do in a match; `count` is set on rows counted up to a target.
 * `perMatch` rows only count when done within this match; `required` is their
 * bar when the row asks for more.
 */
export type ActionRow = {
  text: string;
  count?: number;
  perMatch?: boolean;
  required?: number;
};

/**
 * What to do in each match of a block part, one row per distinct aim. A goal
 * counts for every objective it satisfies, so plain goal and assist amounts
 * collapse to the largest one; technique and scorer rows stay separate.
 */
export const blockActions = (
  objectives: ObjectivePlanBlock["parts"][number]["objectives"],
  t: TranslateFn<"objectives">,
): ActionRow[] => {
  const contributions = objectives.map((o) => o.contribution);
  const plain = { goals: 0, assists: 0, goalsOrAssists: 0 };
  // The highest per-match bar behind each plain amount.
  const bar = { goals: 0, assists: 0, goalsOrAssists: 0 };
  const rows = new Map<string, ActionRow>();
  const put = (row: ActionRow, perMatch: boolean) =>
    rows.set(row.text, {
      ...row,
      perMatch: perMatch || rows.get(row.text)?.perMatch,
    });

  for (const { contribution: c, accumulate } of objectives) {
    const technique = TECHNIQUES.find((x) => x === c.technique);
    const techniqueLabel = technique
      ? t(`technique.${technique}`)
      : c.technique;
    let row: ActionRow | undefined;
    if (c.goals && (techniqueLabel || c.by?.length))
      row = {
        text: techniqueLabel
          ? t("action.goalsTechnique", {
              count: c.goals,
              technique: techniqueLabel,
            })
          : t("action.goals", { count: c.goals }),
        count: c.goals,
      };
    else if (c.assists && (techniqueLabel || c.by?.length))
      row = {
        text: techniqueLabel
          ? t("action.assistsTechnique", {
              count: c.assists,
              technique: techniqueLabel,
            })
          : t("action.assists", { count: c.assists }),
        count: c.assists,
      };
    else if (c.goalsOrAssists && c.by?.length)
      row = {
        text: t("action.goalsOrAssists", { count: c.goalsOrAssists }),
        count: c.goalsOrAssists,
      };
    else {
      plain.goals = Math.max(plain.goals, c.goals ?? 0);
      plain.assists = Math.max(plain.assists, c.assists ?? 0);
      plain.goalsOrAssists = Math.max(
        plain.goalsOrAssists,
        c.goalsOrAssists ?? 0,
      );
      if (!accumulate)
        for (const key of PLAIN) bar[key] = Math.max(bar[key], c[key] ?? 0);
    }
    if (row && c.by?.length)
      row.text = t("action.by", {
        action: row.text,
        player: playerLabel(c.by),
      });
    if (row) put(row, !accumulate);
    if (c.goalsConceded !== undefined)
      put(
        { text: t("action.goalsConceded", { count: c.goalsConceded }) },
        !accumulate,
      );
    if (c.goalDifference)
      put(
        { text: t("action.goalDifference", { count: c.goalDifference }) },
        !accumulate,
      );
  }

  const out: ActionRow[] = [
    ...(contributions.some((c) => c.win) ? [{ text: t("action.win") }] : []),
    ...PLAIN.filter((key) => plain[key]).map((key) => ({
      text: t(`action.${key}`, { count: plain[key] }),
      count: plain[key],
      perMatch: bar[key] > 0,
      required: bar[key] > 0 && bar[key] < plain[key] ? bar[key] : undefined,
    })),
    ...rows.values(),
  ];
  if (out.length === 0) return [{ text: t("action.play") }];
  // Per-match rows first; the sort is stable, so each side keeps its order.
  return out.sort((a, b) => Number(!!b.perMatch) - Number(!!a.perMatch));
};

/** The block's squad in words, from the lineup constraints' labels. */
export const lineupLines = (
  lineup: ObjectivePlanBlock["lineup"],
  t: TranslateFn<"objectives">,
): string[] => [
  ...lineup.starting.map((g: ObjectivePlanLineupGroup) =>
    t("lineup.starting", {
      count: g.nPlayers,
      label: playerLabel(g.constraints),
    }),
  ),
  ...lineup.anywhere.map((g: ObjectivePlanLineupGroup) =>
    g.role === "assister"
      ? t("lineup.assister", { label: playerLabel(g.constraints) })
      : t("lineup.scorer", { label: playerLabel(g.constraints) }),
  ),
];

/** The block's round objectives in words, once each, with the rounds left. */
export const roundLines = (
  parts: ObjectivePlanBlock["parts"],
  t: TranslateFn<"objectives">,
): string[] => {
  const rounds = new Map(
    parts.flatMap((p) =>
      p.objectives.flatMap((o) => (o.round ? [[o.id, o.round] as const] : [])),
    ),
  );
  return [...rounds.values()].map((r) => {
    const times = t("lineup.rounds", { count: r.times });
    return r.perfect
      ? t("lineup.roundPerfect", { rounds: times })
      : t("lineup.roundWins", { count: r.wins, rounds: times });
  });
};

/**
 * A live event's squad rules in words, then its free-text entry note marked
 * as not checked. `ovr` names its inclusive range from whichever bounds it has.
 */
export const eventSquadLines = (
  eventSquad: ObjectivePlanBlock["lineup"]["eventSquad"],
  t: TranslateFn<"objectives">,
): string[] => {
  if (!eventSquad) return [];
  const lines = eventSquad.requirements.map((r) => {
    const values = {
      operator: t(`eventSquad.operator.${r.operator}`),
      value: r.value,
      min: r.minOvr,
      max: r.maxOvr,
    };
    if (r.kind !== "ovr") return t(`eventSquad.${r.kind}`, values);
    if (r.minOvr !== undefined && r.maxOvr !== undefined)
      return t("eventSquad.ovrRange", values);
    return r.minOvr !== undefined
      ? t("eventSquad.ovrAtLeast", values)
      : t("eventSquad.ovrAtMost", values);
  });
  return eventSquad.stringEntry
    ? [...lines, t("eventSquad.notChecked", { entry: eventSquad.stringEntry })]
    : lines;
};
